import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import apiDefinitions from '../../../../../core/api/apiDefinitions';
import { ApiResponse } from './VehicleView';
import { showSuccessAlert, showItemExistsError, showError } from '../../../../../core/alert/sweetAlertConfig';

interface Insurance {
  id: number;
  numberPlate: string;
  insurerCompanyName: string | null;
  policyNumber: string | null;
  policyHolderName: string | null;
  policyHolderAddress: string | null;
  coverageStartDate: string | null;
  coverageEndDate: string | null;
  coverageType: string | null;
  issuedDate: string | null;
  status: string | null;
  images: string[];
  createdBy: string | null;
  createdAt: string | null;
}

interface InsuranceData {
  numberPlate: string;
  insurerCompanyName: string;
  policyNumber: string;
  policyHolderName: string;
  policyHolderAddress: string;
  coverageStartDate: string;
  coverageEndDate: string;
  coverageType: string;
  issuedDate: string;
}

interface FormErrors {
  insurerCompanyName?: string;
  policyNumber?: string;
  policyHolderName?: string;
  policyHolderAddress?: string;
  coverageStartDate?: string;
  coverageEndDate?: string;
  coverageType?: string;
  issuedDate?: string;
  insuranceImages?: string;
}

interface Config {
  coverageTypes: string[];
}

interface VehicleInsuranceProps {
  show: boolean;
  onHide: () => void;
  numberPlate: string;
}

const VehicleInsurance: React.FC<VehicleInsuranceProps> = ({ show, onHide, numberPlate }) => {
  const [insurance, setInsurance] = useState<Insurance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [history, setHistory] = useState<Insurance[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [insuranceData, setInsuranceData] = useState<InsuranceData>({
    numberPlate,
    insurerCompanyName: '',
    policyNumber: '',
    policyHolderName: '',
    policyHolderAddress: '',
    coverageStartDate: '',
    coverageEndDate: '',
    coverageType: '',
    issuedDate: '',
  });
  const [insuranceImages, setInsuranceImages] = useState<File[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [insuranceImagePreviews, setInsuranceImagePreviews] = useState<string[]>([]);
  const maxFileSizeMB = 5;
  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
  const config: Config = {
    coverageTypes: ['Comprehensive', 'Third-Party'],
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        timeZone: 'Asia/Colombo',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleAddModalOpen = () => {
    setShowAddModal(true);
    setFormErrors({});
    setInsuranceData({
      numberPlate,
      insurerCompanyName: '',
      policyNumber: '',
      policyHolderName: '',
      policyHolderAddress: '',
      coverageStartDate: '',
      coverageEndDate: '',
      coverageType: '',
      issuedDate: '',
    });
    setInsuranceImages([]);
    setInsuranceImagePreviews([]);
  };

  const handleAddModalClose = () => {
    setShowAddModal(false);
    setFormErrors({});
    insuranceImagePreviews.forEach(URL.revokeObjectURL);
    setInsuranceImagePreviews([]);
  };

  const handleHistoryModalOpen = async () => {
    setShowHistoryModal(true);
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const response = await apiDefinitions.getInsuranceHistory(numberPlate);
      const data: ApiResponse<Insurance[]> = response.data;
      if (data.status === 'S1000' && data.data) {
        setHistory(data.data);
      } else {
        const message = data.statusDescription || `Failed to fetch insurance history (Status: ${data.status})`;
        setHistoryError(message);
        showError(message);
        setHistory([]);
      }
    } catch (err: any) {
      const message = err.response?.data?.statusDescription || err.message || 'Error fetching insurance history';
      setHistoryError(message);
      showError(message);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryModalClose = () => {
    setShowHistoryModal(false);
    setHistory([]);
    setHistoryError(null);
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    maxSizeMB: number,
    allowedTypes: string[]
  ) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles: File[] = [];
      const errors: FormErrors = {};

      selectedFiles.forEach((file) => {
        if (!allowedTypes.includes(file.type)) {
          errors.insuranceImages = `Invalid file type for ${file.name}. Allowed types: JPEG, PNG, JPG.`;
          return;
        }
        if (file.size > maxSizeMB * 1024 * 1024) {
          errors.insuranceImages = `File ${file.name} exceeds ${maxSizeMB}MB limit.`;
          return;
        }
        validFiles.push(file);
      });

      setFiles(validFiles);
      setFormErrors((prev) => ({ ...prev, insuranceImages: errors.insuranceImages || '' }));
      generatePreviews(validFiles, setInsuranceImagePreviews);
    }
  };

  const generatePreviews = (files: File[], setPreviews: React.Dispatch<React.SetStateAction<string[]>>) => {
    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviews((prev) => {
      prev.forEach(URL.revokeObjectURL);
      return previews;
    });
  };

  const removeFile = (
    index: number,
    files: File[],
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    previews: string[],
    setPreviews: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
    updatedPreviews.forEach(URL.revokeObjectURL);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: FormErrors = {};

    // Existing field validations
    if (!insuranceData.insurerCompanyName) errors.insurerCompanyName = 'Insurer Company Name is required.';
    if (!insuranceData.policyNumber) errors.policyNumber = 'Policy Number is required.';
    if (!insuranceData.policyHolderName) errors.policyHolderName = 'Policy Holder Name is required.';
    if (!insuranceData.policyHolderAddress) errors.policyHolderAddress = 'Policy Holder Address is required.';
    if (!insuranceData.coverageStartDate) errors.coverageStartDate = 'Coverage Start Date is required.';
    if (!insuranceData.coverageEndDate) errors.coverageEndDate = 'Coverage End Date is required.';
    if (!insuranceData.coverageType) errors.coverageType = 'Coverage Type is required.';
    if (!insuranceData.issuedDate) errors.issuedDate = 'Issued Date is required.';

    // Date validations
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day for comparison
    const issuedDate = insuranceData.issuedDate ? new Date(insuranceData.issuedDate) : null;
    const coverageStartDate = insuranceData.coverageStartDate ? new Date(insuranceData.coverageStartDate) : null;
    const coverageEndDate = insuranceData.coverageEndDate ? new Date(insuranceData.coverageEndDate) : null;

    if (issuedDate) {
      if (issuedDate > today) {
        errors.issuedDate = 'Issued Date cannot be in the future.';
      }
      if (coverageStartDate && issuedDate > coverageStartDate) {
        errors.issuedDate = 'Issued Date must be before or equal to Coverage Start Date.';
      }
    }
    if (coverageStartDate && coverageEndDate && coverageStartDate >= coverageEndDate) {
      errors.coverageStartDate = 'Coverage Start Date must be before Coverage End Date.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setFormErrors({});
      const formDataToSend = new FormData();
      formDataToSend.append('request', new Blob([JSON.stringify(insuranceData)], { type: 'application/json' }));
      insuranceImages.forEach((file) => {
        const updatedFile = new File([file], file.name, { type: 'image/png', lastModified: file.lastModified });
        formDataToSend.append('images', updatedFile);
      });

      const response = await apiDefinitions.addVehInsurance(formDataToSend);
      const data: ApiResponse<Insurance> = response.data;
      if (data.status === 'S1000' && data.data) {
        showSuccessAlert("Vehicle insurance added successfully");
        setInsurance(data.data);
        setShowAddModal(false);
        setInsuranceImages([]);
        setInsuranceImagePreviews([]);
      } else if (data.statusDescription?.toLowerCase().includes('already exists')) {
        showItemExistsError("This insurance record already exists.");
      } else {
        showSuccessAlert("Vehicle insurance added successfully");
      }
    } catch (err: any) {
      const message = err.response?.data?.statusDescription || err.message || 'Error adding insurance';
      if (message.toLowerCase().includes('already exists')) {
        showItemExistsError("This insurance record already exists.");
      } else {
        showError(message);
      }
    }
  };

  const downloadInsuranceHistory = () => {
    const formattedData = history.map((item) => ({
      NumberPlate: item.numberPlate,
      InsurerCompanyName: item.insurerCompanyName || 'N/A',
      PolicyNumber: item.policyNumber || 'N/A',
      PolicyHolderName: item.policyHolderName || 'N/A',
      PolicyHolderAddress: item.policyHolderAddress || 'N/A',
      CoverageStartDate: formatDate(item.coverageStartDate),
      CoverageEndDate: formatDate(item.coverageEndDate),
      CoverageType: item.coverageType || 'N/A',
      IssuedDate: formatDate(item.issuedDate),
      Status: item.status || 'N/A',
      CreatedBy: item.createdBy || 'N/A',
      CreatedAt: formatDate(item.createdAt),
      Images: item.images?.join(', ') || 'None',
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'InsuranceHistory');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const fileData = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(fileData, `Insurance_History_${numberPlate}.xlsx`);
  };

  useEffect(() => {
    const fetchInsurance = async () => {
      if (!numberPlate) {
        setError('No number plate provided.');
        showError('No number plate provided.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const response = await apiDefinitions.getInsurance(numberPlate);
        const data: ApiResponse<Insurance> = response.data;
        if (data.status === 'S1000' && data.data) {
          setInsurance(data.data);
        } else {
          const message = data.statusDescription || `Failed to fetch insurance (Status: ${data.status})`;
          setError(message);
          showError(message);
        }
      } catch (err: any) {
        const message = err.response?.data?.statusDescription || err.message || 'Error fetching insurance';
        setError(message);
        showError(message);
      } finally {
        setLoading(false);
      }
    };
    fetchInsurance();
  }, [numberPlate]);

  useEffect(() => {
    generatePreviews(insuranceImages, setInsuranceImagePreviews);
    return () => insuranceImagePreviews.forEach(URL.revokeObjectURL);
  }, [insuranceImages]);

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Insurance - {numberPlate || 'N/A'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <div className="text-center">Loading...</div>}
          {error && <div className="alert alert-danger">{error}</div>}
          {!loading && !error && !insurance && <div className="alert alert-info">No insurance found for this vehicle.</div>}
          {!loading && !error && insurance && (
            <div className="row">
              <div className="col-md-6">
                <h5 className="mb-3">General Information</h5>
                <p><strong>ID:</strong> {insurance.id}</p>
                <p><strong>Insurer Company:</strong> {insurance.insurerCompanyName || 'N/A'}</p>
                <p><strong>Policy Number:</strong> {insurance.policyNumber || 'N/A'}</p>
                <p><strong>Policy Holder Name:</strong> {insurance.policyHolderName || 'N/A'}</p>
                <p><strong>Policy Holder Address:</strong> {insurance.policyHolderAddress || 'N/A'}</p>
                <p><strong>Coverage Type:</strong> {insurance.coverageType || 'N/A'}</p>
                <p><strong>Status:</strong> {insurance.status || 'N/A'}</p>
              </div>
              <div className="col-md-6">
                <p><strong>Created By:</strong> {insurance.createdBy || 'N/A'}</p>
                <p><strong>Created At:</strong> {formatDate(insurance.createdAt)}</p>
                <h5 className="mb-3">Dates</h5>
                <p><strong>Coverage Start Date:</strong> {formatDate(insurance.coverageStartDate)}</p>
                <p><strong>Coverage End Date:</strong> {formatDate(insurance.coverageEndDate)}</p>
                <p><strong>Issued Date:</strong> {formatDate(insurance.issuedDate)}</p>
                <h5>Images</h5>
                {insurance.images && insurance.images.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {insurance.images.map((image, index) => (
                      <Button
                        key={`insurance-image-${index}`}
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleImageClick(image)}
                      >
                        View Image {index + 1}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p>None</p>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleAddModalOpen}>
            Add Vehicle Insurance
          </Button>
          <Button variant="info" onClick={handleHistoryModalOpen}>
            View History
          </Button>
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImageModal} onHide={handleCloseImageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>View Insurance Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage ? (
            <img
              src={selectedImage}
              alt="Insurance Document"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              onError={() => showError('Failed to load image.')}
            />
          ) : (
            <p>No image selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseImageModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAddModal} onHide={handleAddModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Vehicle Insurance - {numberPlate}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleFormSubmit}>
            <div className="container">
              <div className="mb-4">
                <h5>Insurance Records</h5>
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Insurer Company Name</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        value={insuranceData.insurerCompanyName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setInsuranceData({ ...insuranceData, insurerCompanyName: e.target.value })
                        }
                        placeholder="Enter insurer company name"
                      />
                      {formErrors.insurerCompanyName && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.insurerCompanyName}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Policy Number</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        value={insuranceData.policyNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setInsuranceData({ ...insuranceData, policyNumber: e.target.value })
                        }
                        placeholder="Enter policy number"
                      />
                      {formErrors.policyNumber && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.policyNumber}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Policy Holder Name</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        value={insuranceData.policyHolderName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setInsuranceData({ ...insuranceData, policyHolderName: e.target.value })
                        }
                        placeholder="Enter policy holder name"
                      />
                      {formErrors.policyHolderName && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.policyHolderName}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Policy Holder Address</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        value={insuranceData.policyHolderAddress}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setInsuranceData({ ...insuranceData, policyHolderAddress: e.target.value })
                        }
                        placeholder="Enter policy holder address"
                      />
                      {formErrors.policyHolderAddress && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.policyHolderAddress}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Coverage Type</label>
                      <select
                        className="form-select"
                        style={{ width: '100%', padding: '10px' }}
                        value={insuranceData.coverageType}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          setInsuranceData({ ...insuranceData, coverageType: e.target.value })
                        }
                      >
                        <option value="">Select Coverage Type</option>
                        {(config.coverageTypes || []).map((ct: string) => (
                          <option key={ct} value={ct}>
                            {ct}
                          </option>
                        ))}
                      </select>
                      {formErrors.coverageType && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.coverageType}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Coverage Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        value={insuranceData.coverageStartDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setInsuranceData({ ...insuranceData, coverageStartDate: e.target.value })
                        }
                      />
                      {formErrors.coverageStartDate && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.coverageStartDate}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Coverage End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        value={insuranceData.coverageEndDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setInsuranceData({ ...insuranceData, coverageEndDate: e.target.value })
                        }
                      />
                      {formErrors.coverageEndDate && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.coverageEndDate}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Issued Date</label>
                      <input
                        type="date"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        value={insuranceData.issuedDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setInsuranceData({ ...insuranceData, issuedDate: e.target.value })
                        }
                      />
                      {formErrors.issuedDate && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.issuedDate}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="mb-0">Insurance Images (optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        multiple
                        accept={allowedTypes.join(',')}
                        onChange={(e) => handleFileChange(e, setInsuranceImages, maxFileSizeMB, allowedTypes)}
                      />
                      <small className="text-muted">Max {maxFileSizeMB}MB per file, formats: JPEG, PNG, JPG</small>
                      {formErrors.insuranceImages && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.insuranceImages}
                        </div>
                      )}
                      {insuranceImages.length > 0 && (
                        <div className="mt-3">
                          <h6>Selected Files:</h6>
                          <div className="d-flex flex-wrap gap-2">
                            {insuranceImages.map((file, index) => (
                              <div key={index} className="position-relative">
                                <img
                                  src={insuranceImagePreviews[index]}
                                  alt={file.name}
                                  style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                  style={{ padding: '2px 6px' }}
                                  onClick={() =>
                                    removeFile(index, insuranceImages, setInsuranceImages, insuranceImagePreviews, setInsuranceImagePreviews)
                                  }
                                >
                                  ×
                                </button>
                                <div style={{ fontSize: '12px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {file.name}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-success">
                Submit
              </button>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleAddModalClose}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showHistoryModal} onHide={handleHistoryModalClose} size="lg">
        <Modal.Header closeButton>
          <div className="d-flex align-items-center w-100">
            <Modal.Title>Insurance History - {numberPlate}</Modal.Title>
            <div className="ms-auto me-5">
              <Button
                variant="success"
                onClick={downloadInsuranceHistory}
                disabled={history.length === 0}
              >
                Download
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {historyLoading && <div className="text-center">Loading...</div>}
          {historyError && <div className="alert alert-danger">{historyError}</div>}
          {!historyLoading && !historyError && history.length === 0 && (
            <div className="alert alert-info">No insurance history found for this vehicle.</div>
          )}
          {!historyLoading && !historyError && history.length > 0 && (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Policy Number</th>
                  <th>Insurer Company</th>
                  <th>Coverage Start</th>
                  <th>Coverage End</th>
                  <th>Status</th>
                  <th>Images</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record, index) => (
                  <tr key={`history-${index}`}>
                    <td>{record.policyNumber || 'N/A'}</td>
                    <td>{record.insurerCompanyName || 'N/A'}</td>
                    <td>{formatDate(record.coverageStartDate)}</td>
                    <td>{formatDate(record.coverageEndDate)}</td>
                    <td>{record.status || 'N/A'}</td>
                    <td>
                      {record.images && record.images.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {record.images.map((image, imgIndex) => (
                            <Button
                              key={`history-image-${index}-${imgIndex}`}
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleImageClick(image)}
                            >
                              View Image {imgIndex + 1}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        'None'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleHistoryModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VehicleInsurance;